/**
 * Offline write buffer. Decision A26.
 *
 * "Plant is ok with internet" is not the same as "internet never drops". A
 * supervisor who loses a half-entered shift once will go back to paper and
 * never come back, so every write that can be queued is queued.
 *
 * IndexedDB rather than localStorage: writes are structured, can be large, and
 * must survive a tab crash mid-shift.
 */

const DB_NAME = "npi-offline";
const DB_VERSION = 1;
const STORE = "pending-writes";

export interface PendingWrite {
  id: string;
  path: string;
  method: string;
  body: unknown;
  queuedAt: number;
  attempts: number;
  lastError?: string;
}

/** A write the server refused outright, handed back so it can be shown. */
export interface Rejected {
  path: string;
  queuedAt: number;
  status: number;
  body: unknown;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: "id" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
  return dbPromise;
}

function tx<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>
): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const transaction = db.transaction(STORE, mode);
        const request = fn(transaction.objectStore(STORE));
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      })
  );
}

export async function queueWrite(
  entry: Pick<PendingWrite, "path" | "method" | "body">
): Promise<string> {
  const record: PendingWrite = {
    ...entry,
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    queuedAt: Date.now(),
    attempts: 0,
  };
  await tx("readwrite", (store) => store.put(record));
  notify();
  return record.id;
}

export function pendingWrites(): Promise<PendingWrite[]> {
  return tx<PendingWrite[]>("readonly", (store) => store.getAll()).then((rows) =>
    rows.sort((a, b) => a.queuedAt - b.queuedAt)
  );
}

export async function pendingCount(): Promise<number> {
  return (await pendingWrites()).length;
}

async function discard(id: string): Promise<void> {
  await tx("readwrite", (store) => store.delete(id));
  notify();
}

async function recordFailure(entry: PendingWrite, error: string): Promise<void> {
  await tx("readwrite", (store) =>
    store.put({ ...entry, attempts: entry.attempts + 1, lastError: error })
  );
  notify();
}

// -- Change notification, so the sync pill can re-render -------------------

type Listener = (count: number) => void;
const listeners = new Set<Listener>();

export function onQueueChange(listener: Listener): () => void {
  listeners.add(listener);
  pendingCount().then(listener).catch(() => undefined);
  return () => listeners.delete(listener);
}

function notify(): void {
  pendingCount()
    .then((n) => listeners.forEach((l) => l(n)))
    .catch(() => undefined);
}

/**
 * Replay the queue in the order it was written.
 *
 * Order matters: a run that consumes material must not be replayed before the
 * purchase that supplied it, or the negative-stock block will correctly refuse
 * it. On the first failure we stop rather than skipping ahead.
 *
 * A 4xx other than 409 means the server rejected the content itself; retrying
 * will never help, so the entry is dropped and surfaced. A 409 is kept — the
 * missing stock may yet arrive from an earlier queued entry or another user.
 */
export async function flushQueue(
  send: (entry: PendingWrite) => Promise<Response>
): Promise<{ sent: number; failed: number; kept: number; rejected: Rejected[] }> {
  const queue = await pendingWrites();
  let sent = 0;
  let kept = 0;
  const rejected: Rejected[] = [];

  for (let i = 0; i < queue.length; i += 1) {
    const entry = queue[i];
    let response: Response;
    try {
      response = await send(entry);
    } catch {
      // Still offline. Leave this entry and everything after it.
      kept += queue.length - i;
      break;
    }

    if (response.ok) {
      await discard(entry.id);
      sent += 1;
    } else if (response.status === 409) {
      // The stock was not there. It may yet arrive from an earlier queued
      // entry or from another user, so this one is worth keeping.
      await recordFailure(entry, "Blocked — insufficient stock at the time.");
      kept += 1;
    } else if (response.status >= 400 && response.status < 500) {
      // The server rejected the content itself. Retrying will never help, so
      // the entry is dropped — but it is returned to the caller so somebody
      // is told what was lost. Re-queueing it, which is what this used to do,
      // meant the pending count never reached zero and the pill nagged
      // forever about a write that could not succeed.
      rejected.push({
        path: entry.path,
        queuedAt: entry.queuedAt,
        status: response.status,
        body: entry.body,
      });
      await discard(entry.id);
    } else {
      // Server-side problem. Do not burn through the queue against a
      // failing server.
      kept += queue.length - i;
      break;
    }
  }

  return { sent, failed: rejected.length, kept, rejected };
}
